import { useState } from 'react'
import { IconChevronDown, IconAlertTriangle, IconCheck } from '@/components/icons/Icons'

/**
 * El editor del flujo, como lista de pasos.
 *
 * No es un lienzo a propósito: para seis pasos, una lista ordenada se lee igual de bien y
 * cuesta una fracción. El día que el flujo tenga bifurcaciones que no entren en la cabeza,
 * el lienzo se monta sobre este mismo contrato.
 *
 * ─── Lo que este editor NO muestra, NO se pierde ───
 *
 * Cada cambio se fusiona sobre el paso original en vez de reescribirlo. Sin eso, corregir
 * una tilde borraría los campos que esta pantalla no dibuja, y nadie se enteraría hasta
 * que un cliente se quedara esperando.
 */

const ETIQUETAS: Record<string, { nombre: string; que: string }> = {
  mensaje: { nombre: 'Mensaje', que: 'Dice algo y sigue' },
  opciones: { nombre: 'Opciones', que: 'Pregunta y espera que elijan' },
  captura: { nombre: 'Datos', que: 'Pide datos y los extrae de lo que escriban' },
  agenda: { nombre: 'Agenda', que: 'Ofrece horarios reales y agenda' },
  handoff: { nombre: 'A una persona', que: 'Deja de responder y lo toma alguien' },
  fin: { nombre: 'Cierre', que: 'Termina la conversación' },
}

export interface Flujo {
  inicio: string
  nodos: Record<string, any>
  lienzo?: Record<string, { x: number; y: number }>
}

interface Props {
  flujo: Flujo
  onCambio: (flujo: Flujo) => void
  rotos: string[]
  huerfanos: string[]
}

export default function EditorFlujo({ flujo, onCambio, rotos, huerfanos }: Props) {
  const [abierto, setAbierto] = useState<string | null>(flujo.inicio)

  const ids = Object.keys(flujo.nodos)
  const destinos = ['', ...ids]

  /** Fusiona sobre el paso original: lo que no se dibuja, se conserva. */
  const cambiarNodo = (id: string, parcial: Record<string, any>) => {
    onCambio({ ...flujo, nodos: { ...flujo.nodos, [id]: { ...flujo.nodos[id], ...parcial } } })
  }

  const cambiarOpcion = (id: string, i: number, parcial: Record<string, any>) => {
    const ops = [...(flujo.nodos[id].opciones ?? [])]
    ops[i] = { ...ops[i], ...parcial }
    cambiarNodo(id, { opciones: ops })
  }

  const selectDestino = (valor: string, onSet: (v: string) => void, etiqueta: string) => {
    // Un destino que ya no existe se muestra igual, marcado. Esconderlo haría que el
    // select pareciera correcto mientras el flujo está roto.
    const fantasma = valor && !ids.includes(valor)
    return (
      <div className="form-group">
        <label className="form-label">{etiqueta}</label>
        <select className="form-input" value={valor ?? ''} onChange={e => onSet(e.target.value)}>
          <option value="">— termina acá —</option>
          {fantasma && <option value={valor}>{valor} (ya no existe)</option>}
          {destinos.filter(Boolean).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
    )
  }

  return (
    <div className="editor-flujo">
      {rotos.length > 0 && (
        <div className="editor-flujo__aviso editor-flujo__aviso--error">
          <IconAlertTriangle size={15} />
          <div>
            <strong>Hay pasos que mandan a ningún lado.</strong> Mientras esto esté así no se
            puede guardar: quien caiga ahí se queda sin respuesta.
            <ul>{rotos.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        </div>
      )}

      {rotos.length === 0 && huerfanos.length > 0 && (
        <div className="editor-flujo__aviso editor-flujo__aviso--warn">
          <IconAlertTriangle size={15} />
          <div>
            No llega nadie a: {huerfanos.map(h => <code key={h}>{h}</code>)}. No es un error —
            un paso recién creado siempre está así — pero casi siempre es un olvido.
          </div>
        </div>
      )}

      {rotos.length === 0 && huerfanos.length === 0 && (
        <div className="editor-flujo__aviso editor-flujo__aviso--ok">
          <IconCheck size={15} /> Todos los caminos llegan a algún lado.
        </div>
      )}

      <ol className="editor-flujo__pasos">
        {ids.map(id => {
          const nodo = flujo.nodos[id]
          const meta = ETIQUETAS[nodo.tipo] ?? { nombre: nodo.tipo, que: '' }
          const esInicio = id === flujo.inicio
          const estaAbierto = abierto === id

          return (
            <li key={id} className={`editor-flujo__paso${estaAbierto ? ' editor-flujo__paso--abierto' : ''}`}>
              <button
                className="editor-flujo__cabecera"
                onClick={() => setAbierto(estaAbierto ? null : id)}
                type="button"
              >
                <span className={`editor-flujo__tipo editor-flujo__tipo--${nodo.tipo}`}>{meta.nombre}</span>
                <span className="editor-flujo__id">{id}</span>
                {esInicio && <span className="editor-flujo__inicio">entrada</span>}
                <span className="editor-flujo__resumen">
                  {(nodo.mensaje ?? '').slice(0, 60) || meta.que}
                </span>
                <IconChevronDown size={14} />
              </button>

              {estaAbierto && (
                <div className="editor-flujo__cuerpo">
                  {'mensaje' in nodo && (
                    <div className="form-group">
                      <label className="form-label">Qué dice</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        value={nodo.mensaje ?? ''}
                        onChange={e => cambiarNodo(id, { mensaje: e.target.value })}
                      />
                      <p className="editor-flujo__hint">
                        Usá <code>{'{nombre}'}</code> o <code>{'{empresa}'}</code> para
                        poner lo que el cliente ya contó.
                      </p>
                    </div>
                  )}

                  {nodo.tipo === 'opciones' && (
                    <>
                      {(nodo.opciones ?? []).map((op: any, i: number) => (
                        <div className="editor-flujo__opcion" key={i}>
                          <input
                            className="form-input"
                            value={op.etiqueta}
                            onChange={e => cambiarOpcion(id, i, { etiqueta: e.target.value })}
                          />
                          <select
                            className="form-input"
                            value={op.destino}
                            onChange={e => cambiarOpcion(id, i, { destino: e.target.value })}
                          >
                            {!ids.includes(op.destino) && <option value={op.destino}>{op.destino} (ya no existe)</option>}
                            {ids.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                      ))}
                      {nodo.pie !== undefined && (
                        <div className="form-group">
                          <label className="form-label">Línea del final</label>
                          <input className="form-input" value={nodo.pie ?? ''} onChange={e => cambiarNodo(id, { pie: e.target.value })} />
                        </div>
                      )}
                    </>
                  )}

                  {nodo.tipo === 'captura' && (
                    <>
                      <p className="editor-flujo__hint">
                        Datos que pide: {(nodo.campos ?? []).map((c: any) => (
                          <code key={c.clave}>{c.clave}{c.requerido ? '' : '?'}</code>
                        ))}
                      </p>
                      {selectDestino(nodo.siguiente, v => cambiarNodo(id, { siguiente: v }), 'Cuando los tiene, va a')}
                      {selectDestino(nodo.siNoSePudo ?? '', v => cambiarNodo(id, { siNoSePudo: v || undefined }), 'Si se va de tema, va a')}
                    </>
                  )}

                  {nodo.tipo === 'agenda' && (
                    <>
                      <p className="editor-flujo__hint">
                        Los horarios salen de tu agenda real. Usá <code>{'{opciones}'}</code> donde
                        quieras que aparezcan.
                      </p>
                      {selectDestino(nodo.siguiente, v => cambiarNodo(id, { siguiente: v }), 'Si agenda, va a')}
                      {selectDestino(nodo.sinHorarios, v => cambiarNodo(id, { sinHorarios: v }), 'Si no hay horarios, va a')}
                    </>
                  )}

                  {nodo.tipo === 'mensaje' &&
                    selectDestino(nodo.siguiente ?? '', v => cambiarNodo(id, { siguiente: v || undefined }), 'Después va a')}

                  <p className="editor-flujo__conserva">
                    Lo que este editor no muestra de este paso se conserva tal cual.
                  </p>
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
